<template>
  <div>
    <div
      v-if="title !== ''"
      class="candy-bar-title col-12 text-center my-3"
      v-text="title"
    />
    <div
      v-if="slideCount > 0"
      class="d-flex justify-content-center"
    >
      <b-carousel
        :interval="5000"
        :controls="!isMobile"
        :indicators="isMobile"
        class="w-100"
        :class="{
          'no-indicator': slideCount === 1,
        }"
      >
        <b-carousel-slide v-if="isLoading && productList.length === 0">
          <template #img>
            <product-box
              v-for="skeletonIndex in 4"
              :key="'skeletonIndex_' + skeletonIndex"
              :is-loading="isLoading"
              :class="getBootstrapColumnWidthClass(1)"
            />
          </template>
        </b-carousel-slide>
        <template v-else>
          <b-carousel-slide
            v-for="currentSlide in slideCount"
            :key="currentSlide"
          >
            <template #img>
              <product-box
                v-for="product in getCurrentProductBoxList(currentSlide)"
                :key="product.id"
                :product="product"
                :cart-button-only-icon="cartButtonOnlyIcon"
                :is-loading="isLoading"
                :class="getBootstrapColumnWidthClass(currentSlide)"
              />
            </template>
          </b-carousel-slide>
        </template>
      </b-carousel>
    </div>
  </div>
</template>

<script>
import PageNames from '../../constant/pageNames.js'
import { BCarousel, BCarouselSlide } from 'bootstrap-vue'

export default {
  name: 'CandyBar',
  components: {
    'product-box': () => import(/* webpackChunkName: "ProductBox" */ '../ProductBox/ProductBox.vue'),
    'b-carousel': BCarousel,
    'b-carousel-slide': BCarouselSlide,
  },
  props: {
    /**
     * adatok lekérése folyamatban van-e (kell-e skeleton loader)
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * termékek listája amiket meg akarunk jeleniteni
     */
    productList: {
      type: Array,
      required: true,
    },
    /**
     * candybar cime, ha akarunk
     */
    title: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * kosárba gomb csak ikon használat
     */
    cartButtonOnlyIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * képernyőszélesség [px]
       */
      screenWidth: 0,
      /**
       * cikk page-n vagyunk-e
       * ha igen, ott a content nem olyan széles, ott csak 3 terméken renderelünk ki desktop nézetben 4 helyett
       *
       * @type {Boolean}
       */
      isArticlePage: false,
    }
  },
  computed: {
    /**
     * hány product box lesz egy slideon?
     */
    productCountBoxPerSlide() {
      if (this.isArticlePage === false && this.screenWidth > 2000) {
        return Math.min(Math.floor(this.screenWidth / 300), 6)
      }
      const maxDesktopItemCount = this.isArticlePage ? 3 : 4
      return this.isMobile ? 2 : maxDesktopItemCount
    },
    /**
     * hány slide kell?
     */
    slideCount() {
      if (this.isLoading) {
        return 1
      }
      return Math.ceil(this.productList.length / this.productCountBoxPerSlide)
    },
    /**
     * mobil nézet-e
     *
     * @return {boolean}
     */
    isMobile() {
      return this.$mq === 'sm' || this.$mq === 'md'
    },
  },
  created() {
    this.isArticlePage = this.$route.name === PageNames.PAGE_ARTICLE
  },
  mounted() {
    this.screenWidth = window.innerWidth
    window.addEventListener('resize', this.getScreenWidth)
  },
  destroyed() {
    window.removeEventListener('resize', this.getScreenWidth)
  },
  methods: {
    /**
     * képernyőszélesség kivétele data-ba
     */
    getScreenWidth() {
      this.screenWidth = window.innerWidth
    },
    /**
     * jelenlegi slidenak megfelelő számu product box visszaadása
     *
     * @param currentSlide
     * @return {*[]}
     */
    getCurrentProductBoxList(currentSlide) {
      const slideIndex = currentSlide - 1
      const from = slideIndex * this.productCountBoxPerSlide
      const currentProducBoxList = this.productList.slice(from, from + this.productCountBoxPerSlide)
      // kérés volt, hogy ha nincs az utolsó oldalon annyi item mint amennyi elfér, akkor tölsük fel előzőkkel, amig meg nem telik
      // ha van elég item, vagy az összes item kevesebb mint lehet akkor return ami van
      if (currentProducBoxList.length === this.productCountBoxPerSlide || this.productList.length < this.productCountBoxPerSlide) {
        return currentProducBoxList
      }
      // amugy megnézzük mennyi hely maradt ki, és annyit még hozzáteszünk a listánkhoz
      const diffCount = this.productCountBoxPerSlide - currentProducBoxList.length
      return this.productList.slice(from - diffCount, from - diffCount + this.productCountBoxPerSlide)
    },
    /**
     * slidenak és a benne lévő termékeknek megfelelően a kártya mérete
     *
     * @param currentSlide
     * @returns {string}
     */
    getBootstrapColumnWidthClass(currentSlide) {
      const currentSlideBoxes = this.getCurrentProductBoxList(currentSlide)
      const maxDesktopItemWidth = this.isArticlePage ? 4 : 3
      return 'col-' + Math.min(this.isMobile ? 6 : maxDesktopItemWidth, Math.floor(12 / currentSlideBoxes.length))
    },
  },
}
</script>

<style scoped lang="scss">
.candy-bar-title {
  font-size: 32px;
  line-height: 48px;
  font-weight: 800;
  color: #111;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
}

.no-indicator {
  ::v-deep .carousel-control-next,
  ::v-deep .carousel-control-prev,
  ::v-deep .carousel-indicators {
    display: none;
  }
}

::v-deep .carousel-control-next {
  right: -5%;
  width: 5%;
}

::v-deep .carousel-item.active {
  display: flex;
}

::v-deep .carousel-item-next,
::v-deep .carousel-item-prev {
  display: flex;
}

::v-deep .carousel-control-prev {
  left: -5%;
  width: 5%;
}

::v-deep .carousel-control-next-icon,
::v-deep .carousel-control-prev-icon {
  filter: invert(1);
  border: 1px solid #fff;
  border-radius: 20px;
  padding: 10px;
  background-size: 50%;
  background-color: black;
}

::v-deep .carousel-inner {
  padding-bottom: 40px;
}

::v-deep .carousel-indicators {
  margin-bottom: 0;

  li {
    width: 10px;
    height: 10px;
    background-color: #fff;
    border: 2px solid #000;
    border-radius: 20px;
    opacity: 1;

    &.active {
      background-color: #000;
    }
  }
}
</style>
